import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import { ApartmentLeaseTerm, LeaseTermInput } from '../models/Company/types'

export function leaseTermsQueryKey(apartmentId: number) {
    return ['lease-terms', apartmentId] as const
}

function errorMessage(
    err: {
        response?: { data?: Record<string, unknown> | { error?: string } }
    },
    fallback: string,
): string {
    const data = err.response?.data
    if (!data || typeof data !== 'object') return fallback
    if ('error' in data && typeof data.error === 'string') return data.error
    const nonField = (data as { non_field_errors?: string[] }).non_field_errors
    if (Array.isArray(nonField) && nonField[0]) return String(nonField[0])
    const first = Object.values(data)[0]
    if (Array.isArray(first) && first[0]) return String(first[0])
    return fallback
}

export function useLeaseTerms(apartmentId: number | undefined) {
    const queryClient = useQueryClient()

    const query = useQuery<ApartmentLeaseTerm[]>({
        queryKey: leaseTermsQueryKey(apartmentId ?? 0),
        queryFn: async () => {
            const res = await axiosInstance.get(
                `/company/apartment/${apartmentId}/lease-terms/`,
            )
            return res.data
        },
        enabled: apartmentId != null,
    })

    const saveList = (terms: ApartmentLeaseTerm[]) => {
        if (apartmentId == null) return
        queryClient.setQueryData(leaseTermsQueryKey(apartmentId), terms)
    }

    const createMutation = useMutation({
        mutationFn: async (input: LeaseTermInput) => {
            const res = await axiosInstance.post(
                `/company/apartment/${apartmentId}/lease-terms/`,
                input,
            )
            return res.data as ApartmentLeaseTerm
        },
        onSuccess: (created) => {
            const current = queryClient.getQueryData<ApartmentLeaseTerm[]>(
                leaseTermsQueryKey(apartmentId ?? 0),
            )
            saveList(
                [...(current || []), created].sort((a, b) =>
                    b.start_date.localeCompare(a.start_date),
                ),
            )
            toast.success('Lease term added')
        },
        onError: (err: { response?: { data?: Record<string, unknown> } }) => {
            toast.error(errorMessage(err, 'Could not save the lease term'))
        },
    })

    const updateMutation = useMutation({
        mutationFn: async ({
            termId,
            input,
        }: {
            termId: number
            input: LeaseTermInput
        }) => {
            const res = await axiosInstance.patch(
                `/company/apartment/${apartmentId}/lease-terms/${termId}/`,
                input,
            )
            return res.data as ApartmentLeaseTerm
        },
        onSuccess: (updated) => {
            const current =
                queryClient.getQueryData<ApartmentLeaseTerm[]>(
                    leaseTermsQueryKey(apartmentId ?? 0),
                ) || []
            saveList(
                current
                    .map((term) => (term.id === updated.id ? updated : term))
                    .sort((a, b) => b.start_date.localeCompare(a.start_date)),
            )
            toast.success('Lease term updated')
        },
        onError: (err: { response?: { data?: Record<string, unknown> } }) => {
            toast.error(errorMessage(err, 'Could not save the lease term'))
        },
    })

    const deleteMutation = useMutation({
        mutationFn: async (termId: number) => {
            await axiosInstance.delete(
                `/company/apartment/${apartmentId}/lease-terms/${termId}/`,
            )
            return termId
        },
        onSuccess: (termId) => {
            const current =
                queryClient.getQueryData<ApartmentLeaseTerm[]>(
                    leaseTermsQueryKey(apartmentId ?? 0),
                ) || []
            saveList(current.filter((term) => term.id !== termId))
            toast.success('Lease term deleted')
        },
        onError: (err: { response?: { data?: Record<string, unknown> } }) => {
            toast.error(errorMessage(err, 'Could not delete the lease term'))
        },
    })

    return {
        terms: query.data,
        isLoading: query.isLoading,
        createTerm: createMutation.mutateAsync,
        updateTerm: updateMutation.mutateAsync,
        deleteTerm: deleteMutation.mutateAsync,
        isSaving:
            createMutation.isLoading ||
            updateMutation.isLoading ||
            deleteMutation.isLoading,
    }
}
